import React from 'react';
import HelpBadge from './components/HelpBadge';
import './RazzmatazzWorkspace.css';

type FormationItem = { id: string; label: string };

type AssentOverview = {
  product?: string;
  label?: string;
  tagline?: string;
  company_id?: number;
  display_name?: string;
  drop_registered?: boolean;
  drop_state?: string;
  privacy_policy_url?: string;
  opt_out_url?: string;
  event_count?: number;
  recent_events?: Array<{
    id: number;
    event_type: string;
    email: string;
    company_name: string;
    created_at: string;
  }>;
  allowed?: string[];
  refused?: string[];
  formation?: FormationItem[];
  env?: {
    moltsets_key_set?: boolean;
    apollo_key_set?: boolean;
    linkedin_oidc_configured?: boolean;
    stripe_promo_linkedin?: boolean;
  };
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  companyId: number | null;
  language?: 'en' | 'es';
};

const AssentWorkspace: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  companyId,
  language = 'en',
}) => {
  const spanish = language === 'es';
  const [data, setData] = React.useState<AssentOverview | null>(null);
  const [displayName, setDisplayName] = React.useState('');
  const [dropRegistered, setDropRegistered] = React.useState(false);
  const [dropState, setDropState] = React.useState('');
  const [privacyUrl, setPrivacyUrl] = React.useState('');
  const [optOutUrl, setOptOutUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');

  const headers = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!companyId) {
      setData(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/assent/overview?company_id=${companyId}`,
        { headers },
      );
      const payload = (await response.json().catch(() => ({}))) as AssentOverview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `HTTP ${response.status}`);
      }
      setData(payload);
      setDisplayName(String(payload.display_name || ''));
      setDropRegistered(Boolean(payload.drop_registered));
      setDropState(String(payload.drop_state || ''));
      setPrivacyUrl(String(payload.privacy_policy_url || ''));
      setOptOutUrl(String(payload.opt_out_url || ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [companyId, gatewayBaseUrl, headers]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/assent/tenant`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: companyId,
          display_name: displayName.trim(),
          drop_registered: dropRegistered,
          drop_state: dropState.trim(),
          privacy_policy_url: privacyUrl.trim(),
          opt_out_url: optOutUrl.trim(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as AssentOverview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Save failed');
      }
      setData(payload);
      setNote(spanish ? 'Assent guardado.' : 'Assent saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!companyId) {
    return (
      <p className="razz-empty" role="status">
        {spanish ? 'Elige un proyecto para Assent.' : 'Pick a project for Assent.'}
      </p>
    );
  }

  return (
    <div className="razz-pipeline">
      <header className="razz-card">
        <p className="razz-kicker">Assent</p>
        <h3>
          {spanish ? 'Inteligencia con asentimiento' : 'First-party assent intelligence'}{' '}
          <HelpBadge label={spanish ? 'Qué es Assent' : 'What is Assent'}>
            <p>
              {spanish
                ? 'Assent es el producto de datos de NextCompli: empresa desde IP, identidad solo tras asentimiento, y checklist DROP. No resuelve personas anónimas.'
                : 'Assent is NextCompli’s data product: company-from-IP, identity only after assent, and a DROP checklist. It will not resolve anonymous people.'}
            </p>
          </HelpBadge>
        </h3>
        <p className="razz-muted">{data?.tagline || ''}</p>
      </header>

      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {note ? <p className="razz-muted" role="status">{note}</p> : null}

      <form
        className="razz-card"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="demo-field">
          <span>{spanish ? 'Nombre de entidad Assent' : 'Assent entity display name'}</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Assent"
            disabled={busy}
          />
        </label>
        <label className="razz-intake-source-row">
          <input
            type="checkbox"
            checked={dropRegistered}
            onChange={(e) => setDropRegistered(e.target.checked)}
            disabled={busy}
          />
          <span>
            <strong>
              {spanish ? 'Registrado en DROP (u otro estado)' : 'Registered on DROP (or other state)'}
            </strong>
          </span>
        </label>
        <label className="demo-field">
          <span>{spanish ? 'Estado(s) de registro' : 'Registration state(s)'}</span>
          <input
            value={dropState}
            onChange={(e) => setDropState(e.target.value)}
            placeholder="CA"
            disabled={busy}
          />
        </label>
        <label className="demo-field">
          <span>{spanish ? 'URL de privacidad' : 'Privacy policy URL'}</span>
          <input
            value={privacyUrl}
            onChange={(e) => setPrivacyUrl(e.target.value)}
            placeholder="https://…"
            disabled={busy}
          />
        </label>
        <label className="demo-field">
          <span>{spanish ? 'URL de exclusión / no vender' : 'Opt-out / Do Not Sell URL'}</span>
          <input
            value={optOutUrl}
            onChange={(e) => setOptOutUrl(e.target.value)}
            placeholder="https://…"
            disabled={busy}
          />
        </label>
        <button type="submit" className="dashboard-btn" disabled={busy}>
          {spanish ? 'Guardar' : 'Save'}
        </button>
      </form>

      <section className="razz-card">
        <h3>{spanish ? 'Permitido' : 'Allowed'}</h3>
        <ul>
          {(data?.allowed || []).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <h3>{spanish ? 'Rechazado' : 'Refused'}</h3>
        <ul>
          {(data?.refused || []).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="razz-card">
        <h3>{spanish ? 'Formación / licencia' : 'Formation / license checklist'}</h3>
        <ul>
          {(data?.formation || []).map((item) => (
            <li key={item.id}>{item.label}</li>
          ))}
        </ul>
      </section>

      <section className="razz-card">
        <h3>
          {spanish ? 'Eventos de asentimiento' : 'Assent events'}{' '}
          <span className="razz-muted">({data?.event_count ?? 0})</span>
        </h3>
        <ul>
          {(data?.recent_events || []).length === 0 ? (
            <li className="razz-muted">
              {spanish ? 'Aún no hay eventos.' : 'No events yet.'}
            </li>
          ) : (
            (data?.recent_events || []).map((row) => (
              <li key={row.id}>
                <strong>{row.event_type}</strong>
                {row.email ? ` · ${row.email}` : ''}
                {row.company_name ? ` · ${row.company_name}` : ''}
                <span className="razz-muted"> · {row.created_at}</span>
              </li>
            ))
          )}
        </ul>
        <p className="razz-muted">
          {[
            data?.env?.linkedin_oidc_configured
              ? (spanish ? 'LinkedIn OIDC listo' : 'LinkedIn OIDC ready')
              : (spanish ? 'LinkedIn OIDC pendiente' : 'LinkedIn OIDC pending'),
            data?.env?.stripe_promo_linkedin
              ? (spanish ? 'Promo Stripe listo' : 'Stripe promo set')
              : (spanish ? 'Promo Stripe pendiente' : 'Stripe promo pending'),
            data?.env?.moltsets_key_set || data?.env?.apollo_key_set
              ? (spanish ? 'Clave de enriquecimiento detectada' : 'Enrich key detected')
              : (spanish ? 'Sin clave de enriquecimiento' : 'No enrich key'),
          ].join(' · ')}
        </p>
      </section>
    </div>
  );
};

export default AssentWorkspace;
